import { Fragment, useMemo, useState } from 'react'
import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react'

import type { GradeKind, TeaGrade } from '@/lib/api/tea-grade-api'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { defineListFilters } from '@/components/common/filters/core'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { TeaGradeStockRows } from '@/components/tea-grades/tea-grade-stock-rows'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateTeaGrade,
    useDeleteTeaGrade,
    usePatchTeaGrade,
    useTeaGradeStockLots,
    useTeaGradesList,
} from '@/hooks/use-tea-grade-query'
import { useTeaGradeSearchTracking } from '@/hooks/use-tea-grade-search-tracking'
import { useTeaGradeStockTracking } from '@/hooks/use-tea-grade-stock-tracking'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * Ô tìm khai qua bộ lọc dùng chung của màn danh sách: từ khoá được đưa lên URL
 * (chia sẻ link hay bấm Back không mất từ khoá), debounce + chip + nút xoá lọc
 * đã có sẵn trong `textFilter`, và thanh lọc hiển thị giống 50 màn còn lại.
 */
export const GRADE_CATALOG_FILTERS = defineListFilters({
    q: textFilter({
        label: m.common_search,
        placeholder: m.tea_grade_search_placeholder,
    }),
})

export type GradeCatalogSearch = ReturnType<
    typeof GRADE_CATALOG_FILTERS.validateSearch
>

interface GradeCatalogPageProps {
    kind: GradeKind
    search: GradeCatalogSearch
    onSearchChange: (
        next: GradeCatalogSearch,
        opts?: { replace?: boolean },
    ) => void
}

/**
 * M-39: kho phân loại và kho lấy hương mỗi kho một danh mục phân loại riêng
 * (hai bảng), cùng hình dạng — nên dùng chung màn này, chỉ khác danh mục đọc
 * ghi, quyền sửa, tiêu đề và kho của cột tồn.
 */
const COPY = {
    sorting: {
        title: m.tea_grade_title,
        subtitle: m.tea_grade_subtitle,
        writePermission: 'tea_grades.write',
    },
    scenting: {
        title: m.scenting_grade_title,
        subtitle: m.scenting_grade_subtitle,
        writePermission: 'scenting_grades.write',
    },
} as const

interface FormState {
    code: string
    name: string
    sort_order: number
    is_active: boolean
    // Cờ chè tấm (M-74) — chỉ danh mục kho lấy hương có, nên chỉ gửi khi
    // kind = 'scenting'.
    is_broken?: boolean
}

const EMPTY_FORM: FormState = {
    code: '',
    name: '',
    sort_order: 0,
    is_active: true,
}

export function GradeCatalogPage({
    kind,
    search,
    onSearchChange,
}: GradeCatalogPageProps) {
    const copy = COPY[kind]
    const { hasPermission } = useAuth()
    const canWrite = hasPermission(copy.writePermission)

    const { values: filters, barProps } = useListFilters({
        def: GRADE_CATALOG_FILTERS,
        search,
        onSearchChange,
    })

    const { data, isLoading } = useTeaGradesList(undefined, kind)
    const all = useMemo(() => data?.data ?? [], [data])
    // Sắp theo TÊN, không theo sort_order: bảng không còn hiện cột thứ tự nên
    // một thứ tự người dùng không nhìn thấy sẽ trông như sắp ngẫu nhiên.
    // localeCompare 'vi' để "Ấm" nằm đúng chỗ và "CHÈ 140" đứng cạnh "Chè 145"
    // thay vì bị chữ hoa đẩy lên đầu danh sách.
    // Lọc tại chỗ (danh mục vài chục dòng, đã tải sẵn) → gõ tới đâu thấy tới
    // đó, không chờ request.
    const rows = useMemo(() => {
        const q = filters.q.trim().toLowerCase()
        const filtered = q
            ? all.filter(
                  (g) =>
                      g.name.toLowerCase().includes(q) ||
                      g.code.toLowerCase().includes(q),
              )
            : all
        return [...filtered].sort((a, b) =>
            a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }),
        )
    }, [all, filters.q])

    // Đo ô tìm: tìm xong có ra loại người dùng cần không. `empty` nhiều bất
    // thường là tín hiệu danh mục đang thiếu loại mà xưởng vẫn dùng — đúng ca
    // A160/A150. Contract: `docs/analytics/tea-grade-stock.md`.
    useTeaGradeSearchTracking({
        query: filters.q,
        matchCount: rows.length,
        isLoading,
        catalog: kind,
    })

    // Số cột của bảng — dòng rỗng và dòng bung tồn đều trải hết bảng.
    const columnCount = canWrite ? 5 : 4

    // Bung một phân loại tại một thời điểm, giống màn vị trí kho: mỗi dòng bung
    // là một request tồn. Dòng vừa bị xoá (hoặc chưa tải xong danh sách) thì
    // coi như không bung, để không giữ request sống cho dòng không hiển thị.
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const expandedGradeId =
        expandedId && rows.some((r) => r.id === expandedId) ? expandedId : null

    // enabled: !!id trong hook → không có dòng nào bung thì không gọi API.
    const {
        data: stockLots,
        isPending: isStockPending,
        isError: isStockError,
        isFetching: isStockFetching,
        refetch: refetchStock,
    } = useTeaGradeStockLots(expandedGradeId ?? '', kind)
    // Bấm Thử lại: query vẫn ở trạng thái lỗi trong lúc tải lại — hiện "đang
    // tải" thay vì để nguyên thông báo lỗi như thể nút không phản hồi.
    const isStockLoading = isStockPending || (isStockError && isStockFetching)

    const { trackOpened } = useTeaGradeStockTracking({
        expandedId: expandedGradeId,
        isLoading: isStockLoading,
        isError: isStockError,
        rowCount: stockLots?.length ?? 0,
        catalog: kind,
    })

    const toggleStock = (id: string) => {
        if (expandedGradeId === id) {
            setExpandedId(null)
            return
        }
        setExpandedId(id)
        trackOpened()
    }

    const [editing, setEditing] = useState<TeaGrade | null>(null)
    const [open, setOpen] = useState(false)

    const createMut = useCreateTeaGrade(kind)
    const patchMut = usePatchTeaGrade(kind)
    const deleteMut = useDeleteTeaGrade(kind)

    const openCreate = () => {
        setEditing(null)
        setOpen(true)
    }
    const openEdit = (row: TeaGrade) => {
        setEditing(row)
        setOpen(true)
    }
    const handleDelete = (row: TeaGrade) => {
        // Prompt tiếng Việt — key có sẵn với placeholder {name}.
        if (!window.confirm(m.tea_grade_delete_confirm({ name: row.name }))) {
            return
        }
        deleteMut.mutate(row.id)
    }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={copy.title()}
                subtitle={copy.subtitle()}
                utilityActions={
                    canWrite && (
                        <Button size="sm" onClick={openCreate}>
                            <Plus className="size-4 mr-1" />
                            {m.tea_grade_create()}
                        </Button>
                    )
                }
            />

            <ListFilterBar {...barProps} />

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-32">
                                {m.tea_grade_code()}
                            </TableHead>
                            <TableHead className="min-w-40">
                                {m.tea_grade_name()}
                            </TableHead>
                            {/* Tồn của kho sở hữu danh mục (M-39): kho kia
                                có danh mục riêng, không hiện ở đây. */}
                            <TableHead className="w-32 text-right">
                                {m.tea_grade_col_stock()}
                            </TableHead>
                            <TableHead className="w-28 text-center">
                                {m.tea_grade_is_active()}
                            </TableHead>
                            {canWrite && <TableHead className="w-24" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columnCount}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {filters.q.trim()
                                        ? m.tea_grade_search_empty()
                                        : m.tea_grade_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => {
                                const isExpanded = expandedGradeId === row.id
                                return (
                                    <Fragment key={row.id}>
                                        {/* Bấm cả dòng để bung tồn (vùng bấm
                                            lớn trên máy trạm kho); nút mã là
                                            lối vào cho bàn phím/trình đọc màn
                                            hình. Sửa/Xoá chặn nổi bọt để không
                                            bung nhầm. */}
                                        <TableRow
                                            data-testid="tea-grade-row"
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => toggleStock(row.id)}
                                        >
                                            <TableCell className="font-mono">
                                                <button
                                                    type="button"
                                                    aria-expanded={isExpanded}
                                                    className="flex items-center gap-1.5 text-left hover:underline"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleStock(row.id)
                                                    }}
                                                >
                                                    <ChevronRight
                                                        aria-hidden
                                                        className={twJoin(
                                                            'size-4 shrink-0 text-muted-foreground transition-transform',
                                                            isExpanded &&
                                                                'rotate-90',
                                                        )}
                                                    />
                                                    {row.code}
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                {row.name}
                                                {kind === 'scenting' &&
                                                    row.is_broken && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="ml-2"
                                                        >
                                                            {m.tea_grade_is_broken()}
                                                        </Badge>
                                                    )}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatQty(stockOf(row, kind))}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge
                                                    variant={
                                                        row.is_active
                                                            ? 'default'
                                                            : 'secondary'
                                                    }
                                                >
                                                    {row.is_active ? '✓' : '—'}
                                                </Badge>
                                            </TableCell>
                                            {canWrite && (
                                                <TableCell
                                                    className="text-right space-x-1"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            openEdit(row)
                                                        }
                                                    >
                                                        <Pencil className="size-4" />
                                                    </Button>
                                                    {(() => {
                                                        const inUseReason =
                                                            deleteBlockReason(
                                                                row,
                                                                kind,
                                                            )
                                                        // Wrap span (inline-block) để `title` tooltip vẫn hiện khi
                                                        // Button ở trạng thái disabled — HTML native quirk: disabled
                                                        // button không dispatch mouse events cho tooltip riêng nó.
                                                        return (
                                                            <span
                                                                title={
                                                                    inUseReason ??
                                                                    undefined
                                                                }
                                                                className="inline-block"
                                                            >
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() =>
                                                                        handleDelete(
                                                                            row,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        deleteMut.isPending ||
                                                                        inUseReason !==
                                                                            null
                                                                    }
                                                                    className={
                                                                        inUseReason
                                                                            ? 'pointer-events-none'
                                                                            : undefined
                                                                    }
                                                                >
                                                                    <Trash2 className="size-4 text-red-500" />
                                                                </Button>
                                                            </span>
                                                        )
                                                    })()}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        {isExpanded && (
                                            <TeaGradeStockRows
                                                colSpan={columnCount}
                                                lots={stockLots}
                                                isLoading={isStockLoading}
                                                isError={isStockError}
                                                onRetry={() => {
                                                    void refetchStock()
                                                }}
                                            />
                                        )}
                                    </Fragment>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {rows.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    {m.tea_grade_total({ count: String(rows.length) })}
                </p>
            )}

            <TeaGradeDialog
                kind={kind}
                open={open}
                onOpenChange={setOpen}
                initial={editing}
                submitting={createMut.isPending || patchMut.isPending}
                onSubmit={async (values) => {
                    try {
                        if (editing) {
                            await patchMut.mutateAsync({
                                id: editing.id,
                                data: values,
                            })
                        } else {
                            await createMut.mutateAsync(values)
                        }
                        setOpen(false)
                    } catch {
                        // Toast hiện qua onError của mutation — dialog giữ mở để sửa.
                    }
                }}
            />
        </div>
    )
}

function TeaGradeDialog({
    kind,
    open,
    onOpenChange,
    initial,
    submitting,
    onSubmit,
}: {
    kind: GradeKind
    open: boolean
    onOpenChange: (v: boolean) => void
    initial: TeaGrade | null
    submitting: boolean
    onSubmit: (values: FormState) => Promise<void>
}) {
    // Local form state — reset khi initial đổi (edit vs create).
    // Pattern "controlled by initial key" (React docs) — không dùng useEffect
    // vì form ngắn, chỉ 4 field, mỗi lần initial đổi thì sync bằng key check.
    const initialKey = initial?.id ?? 'new'
    const [key, setKey] = useState(initialKey)
    const [form, setForm] = useState<FormState>(() =>
        initialToForm(initial, kind),
    )
    const [errors, setErrors] = useState<FormErrors>({})
    if (key !== initialKey) {
        setKey(initialKey)
        setForm(initialToForm(initial, kind))
        setErrors({})
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {initial ? m.tea_grade_edit() : m.tea_grade_create()}
                    </DialogTitle>
                </DialogHeader>
                <form
                    className="space-y-3"
                    onSubmit={(e) => {
                        e.preventDefault()
                        // Không disable nút Lưu vì form chưa hợp lệ (rule repo):
                        // bấm được, rồi báo lỗi ngay cạnh ô sai.
                        const next = validateForm(form)
                        setErrors(next)
                        if (next.code || next.name) return
                        void onSubmit({
                            code: form.code.trim(),
                            name: form.name.trim(),
                            sort_order: form.sort_order,
                            is_active: form.is_active,
                            ...(kind === 'scenting'
                                ? { is_broken: form.is_broken ?? false }
                                : {}),
                        })
                    }}
                >
                    <div>
                        <Label htmlFor="tea-grade-code">
                            {m.tea_grade_code()} *
                        </Label>
                        <Input
                            id="tea-grade-code"
                            value={form.code}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    code: e.target.value.toUpperCase(),
                                })
                            }
                            placeholder={m.tea_grade_code_placeholder()}
                            className="font-mono"
                            aria-invalid={errors.code ? true : undefined}
                            aria-describedby={
                                errors.code ? 'tea-grade-code-error' : undefined
                            }
                        />
                        {errors.code && (
                            <p
                                id="tea-grade-code-error"
                                className="mt-1 text-xs text-destructive"
                            >
                                {errors.code}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label htmlFor="tea-grade-name">
                            {m.tea_grade_name()} *
                        </Label>
                        <Input
                            id="tea-grade-name"
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder={m.tea_grade_name_placeholder()}
                            aria-invalid={errors.name ? true : undefined}
                            aria-describedby={
                                errors.name ? 'tea-grade-name-error' : undefined
                            }
                        />
                        {errors.name && (
                            <p
                                id="tea-grade-name-error"
                                className="mt-1 text-xs text-destructive"
                            >
                                {errors.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label>{m.tea_grade_sort_order()}</Label>
                        <Input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    sort_order: Number(e.target.value) || 0,
                                })
                            }
                            className="w-24"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.is_active}
                            onCheckedChange={(v) =>
                                setForm({ ...form, is_active: v })
                            }
                        />
                        <Label>{m.tea_grade_is_active()}</Label>
                    </div>
                    {kind === 'scenting' && (
                        <div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="tea-grade-is-broken"
                                    aria-label={m.tea_grade_is_broken()}
                                    checked={form.is_broken ?? false}
                                    onCheckedChange={(v) =>
                                        setForm({ ...form, is_broken: v })
                                    }
                                />
                                <Label htmlFor="tea-grade-is-broken">
                                    {m.tea_grade_is_broken()}
                                </Label>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {m.tea_grade_is_broken_hint()}
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button type="submit" disabled={submitting}>
                            {submitting ? '...' : m.common_save()}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

/** Tồn của kho sở hữu danh mục — cặp field của kho kia luôn 0 (M-39). */
function stockOf(row: TeaGrade, kind: GradeKind): number {
    return kind === 'scenting' ? row.scenting_stock : row.sorting_stock
}

/**
 * Trả về lý do (chuỗi tiếng Việt) khi grade không được xoá vì đang có lô của
 * kho sở hữu danh mục sử dụng. `null` = xoá được.
 */
function deleteBlockReason(row: TeaGrade, kind: GradeKind): string | null {
    const count =
        kind === 'scenting' ? row.scenting_lots_count : row.sorting_lots_count
    if (count === 0) return null
    const detail =
        kind === 'scenting'
            ? m.tea_grade_delete_blocked_scenting({ count: String(count) })
            : m.tea_grade_delete_blocked_sorting({ count: String(count) })
    return m.tea_grade_delete_blocked_prefix() + ' ' + detail
}

interface FormErrors {
    code?: string
    name?: string
}

function validateForm(form: FormState): FormErrors {
    const errors: FormErrors = {}
    if (!form.code.trim()) errors.code = m.tea_grade_code_required()
    if (!form.name.trim()) errors.name = m.tea_grade_name_required()
    return errors
}

function initialToForm(initial: TeaGrade | null, kind: GradeKind): FormState {
    const broken =
        kind === 'scenting' ? { is_broken: initial?.is_broken ?? false } : {}
    if (!initial) return { ...EMPTY_FORM, ...broken }
    return {
        code: initial.code,
        name: initial.name,
        sort_order: initial.sort_order,
        is_active: initial.is_active,
        ...broken,
    }
}
